<template>
    <div>
        <fe-overlay v-if="loading" loader text="Loading AI Overview..." color="white"/>
        <explore-widget
            widgetTitle="Chronic Absence"
            toolTip="Chronic absence is when a student is absent (excused or unexcused) for at least 10% of the days. The overall rate indicates the percentage of students who are chronically absent in a given time period."
            dataTestBaseString="chronic-absence"
            :cardHeight="launchpadWidgetHeights.thirdRow"
            :countCard="countCard"
            @exploreClick="caExploreClicked"
            ref="chronic-absence-explore-widget"
        >
            <template v-slot:widget-body>
                <div v-if="!ready">
                    <fe-spinner class="spinner"/>
                </div>
                <div class="center-ca-data" v-else>
                    <div v-if="error" id="ca-change-container">
                        <v-icon style="float: left; padding-right: 4px;" color="#bf0f00 !important">far fa-exclamation-circle</v-icon>
                        <div style="color: #bf0f00; float: right;">Data temporarily unavailable</div>
                    </div>
                    <div v-if="unavailable" id="ca-change-container">
                        <v-icon style="float: left; padding-right: 4px;" color="#136eb3 !important">fal fa-info-circle</v-icon>
                        <div style="float: right; color:#136eb3">Attendance data not yet available</div>
                    </div>
                    <v-btn v-if="explainItEnabled" rounded outlined
                        class="gradient-outline-box mt-4"
                        @click="getExplainIt"
                        data-test="chronic-absence-explain-it-btn"
                        :disabled="loading"
                    >
                        <v-icon class="far fa-sparkles mr-2" color="#2b87ff !important" size="16"></v-icon>
                        <span style="font-size:16px">Explain It</span>
                    </v-btn>
                </div>
            </template>
        </explore-widget>
        <v-dialog v-model="openExplainIt"
            :footer="false"
            :persistent="true"
            :retain-focus="false"
            :overlay="false"
            width="90vw"
            content-class="explain-it-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="explainItTitle"
            aria-describedby="explainItBody"
        >
            <v-card style="height: 550px;">
                <v-card-title id="explainItTitle">
                    <div class="d-flex" style="width: 100%;">
                        <div class="d-flex align-center ml-4"><v-icon class="far fa-sparkles mr-2" color="#006C90 !important" size="20"></v-icon>Explain it</div>
                        <v-spacer/>
                        <fe-icon-btn class="close-icon" useIcon="fal fa-times" @click="openExplainIt = false" aria-label="Close dialog"/>
                    </div>
                </v-card-title>
                <v-card-text class="d-flex" id="explainItBody">
                    <explore-widget
                        widgetTitle="Chronic Absence"
                        toolTip="Chronic absence is when a student is absent (excused or unexcused) for at least 10% of the days. The overall rate indicates the percentage of students who are chronically absent in a given time period."
                        dataTestBaseString="chronic-absence"
                        :cardHeight="launchpadWidgetHeights.thirdRow"
                        :countCard="countCard"
                        @exploreClick="caExploreClicked"
                        ref="chronic-absence-explore-widget"
                        style="width:50%"
                    >
                        <template v-slot:widget-body>
                            <div v-if="!ready">
                                <fe-spinner class="spinner"/>
                            </div>
                            <div class="center-ca-data" v-else>
                                <div v-if="error" id="ca-change-container">
                                    <v-icon style="float: left; padding-right: 4px;" color="#bf0f00 !important">far fa-exclamation-circle</v-icon>
                                    <div style="color: #bf0f00; float: right;">Data temporarily unavailable</div>
                                </div>
                                <div v-if="unavailable" id="ca-change-container">
                                    <v-icon style="float: left; padding-right: 4px;" color="#136eb3 !important">fal fa-info-circle</v-icon>
                                    <div style="float: right; color:#136eb3">Attendance data not yet available</div>
                                </div>
                            </div>
                        </template>
                    </explore-widget>
                    <fe-card
                        class="alt-card explain-it-card"
                        style="width: 50%"
                    >
                        <template v-slot:title-text>
                            <span class="d-flex fe-card-title-text align-center" style="font-size: 16px;">
                                <v-icon class="far fa-sparkles mr-2" color="#006C90 !important" size="14"></v-icon>AI Overview
                                <fe-info-tip class="ml-2 mb-1" tooltip="This is an experimental feature. Verify important information. 
                                    Demographic explanations do not cover ethnicity or gender."></fe-info-tip>
                            </span>
                        </template>

                        <template v-slot:title-toolbar>
                            <fe-btn id="explainItCopyBtn" usage="secondary" class="copy-btn mb-1" @click="copyText()" data-test="chronic-absence-copy-btn">
                                <v-icon class="far fa-copy mr-2" color="#006c90 !important" size="14"></v-icon>Copy
                            </fe-btn>
                        </template>

                        <template v-slot:body>
                            <div style="padding: 16px; flex-grow: 1; overflow: scroll; max-height: 380px;">
                                <div v-if="explainItError" class="d-flex justify-center">
                                    <alert-icon usage="error" />
                                </div>
                                <span id="aiExplainItText" style="white-space: pre-wrap;">{{ explainItText }}</span>
                            </div>
                        </template>
                    </fe-card>
                </v-card-text>
            </v-card>
        </v-dialog>
    </div>
</template>

<script>
import { mapState } from 'vuex'
import ExploreWidget from './ExploreWidget.vue'
import AlertIcon from '@/components/common/AlertIcon.vue'

export default {
    name: 'ChronicAbsenceCard',

    components: {
        ExploreWidget,
        AlertIcon,
    },

    data() {
        return {
            percentChange: null,
            error: false,
            unavailable: false,
            ready: false,
            countCard: {
                text: 'Students chronically absent this school year',
                show: false,
                change: null,
                error: false,
                unavailable: false,
                count: null,
                percentage: true,
                displayUpDownArrow: null,
                title: 'chronic-absence'
            },
            openExplainIt: false,
            explainItText: '',
            explainItError: false,
            loading: false,
        }
    },

    computed: {
        ...mapState('global', ['launchpadWidgetHeights']),
        explainItEnabled(){
            return this.$store.getters['flags/flags']['e-c-ai-explain-it'] === true
        },
    },

    mounted() {
        this.loadCAData()
    },

    methods: {
        loadCAData() {
            this.$axios.get(this.$models.getUrl('chronicAbsenceSummary','read'))
                .then(response => {
                    if (response.status !== 200) {
                        this.error = true
                        this.ready = true
                        this.countCard.show = true
                        this.countCard.error = true
                        return
                    }
                    if (response.data.chronically_absent_percentage === null && response.data.percent_change === null) {
                        this.unavailable = true
                        this.ready = true
                        this.countCard.show = true
                        this.countCard.unavailable = true
                        return
                    }
                    this.error = false,
                    this.unavailable = false
                    this.ready = true
                    this.countCard.change = response.data.percent_change
                    this.countCard.displayChange = response.data.percent_change.toString().includes('-') ? response.data.percent_change.toString().substring(1) : response.data.percent_change
                    this.countCard.displayUpDownArrow = response.data.percent_change < 0 ? 'down' : 'up'
                    this.countCard.show = true
                    this.countCard.error = false
                    this.countCard.unavailable = false
                    this.countCard.count = response.data.chronically_absent_percentage
                })
        },

        caExploreClicked() {
            this.$router.push({path: 'ChronicAbsence'})
        },

        copyText() {
            const text = document.getElementById('aiExplainItText')
            if (!text) return

            // Try modern clipboard API first
            if (navigator.clipboard && navigator.clipboard.writeText) {
                navigator.clipboard.writeText(text.textContent)
                    .catch(err => {
                        console.error('Failed to copy text:', err)
                        this.fallbackCopyText(text.textContent)
                    })
            } else {
                // Fallback for older browsers or non-secure contexts
                this.fallbackCopyText(text.textContent)
            }
        },

        fallbackCopyText(textContent) {
            const textarea = document.createElement('textarea')
            textarea.value = textContent
            textarea.style.position = 'fixed'
            textarea.style.opacity = '0'
            document.body.appendChild(textarea)
            textarea.select()
            try {
                document.execCommand('copy')
            } catch (err) {
                console.error('Fallback copy failed:', err)
            }
            document.body.removeChild(textarea)
        },

        getExplainIt() {
            this.loading = true
            this.$modelPost('chronicAbsenceExplainIt', 'create')
                .then(response => {
                    if(response?.success === false) {
                        this.explainItText = response?.message
                        this.explainItError = true
                    } else {
                        this.explainItText = response?.explanation
                        this.explainItError = false
                    }
                })
                .catch(() => {
                    this.explainItText = "There was an error connecting to the AI tool. Please try again."
                    this.explainItError = true
                })
                .finally(() => {
                    this.openExplainIt = true
                    this.loading = false
                })
        }
    }
}
</script>

<style lang="scss">
    .explain-it-dialog {
        overflow: hidden;
    }
</style>

<style scoped lang="scss">

    .center-ca-data {
        text-align: center;

        .ca-percentage {
            margin: 5px;
            font-size: 64px;
            font-weight: 700;
        }

        .ca-no-data {
            color: #495b8f;
        }

        .ca-text {
            margin: 5px;
            padding-top: 24px;
            font-size: 16px;
            color: #495b8f;
        }

        .ca-change {
            color: #495b8f !important;
        }

        #ca-change-container {
            margin-left: auto;
            margin-right: auto;
            max-width:fit-content;
            font-size: 16px;
            color: #495b8f;
        }

        #ca-percent-change {
            float: left;
            padding-right: 4px;
        }

        #ca-percent-change-text {
            float: right;
        }
    }

    .spinner {
        position: absolute;
        left: 0;
        right: 0;
        margin: auto;
        line-height: 250px !important;
    }

    .gradient-outline-box {
        padding: 20px !important;
        border: 1px solid transparent;
        border-radius: 30px;
        background:
            linear-gradient(#fff, #fff) padding-box, /* Inner solid color */
            linear-gradient(to right, #43D0F5, #2B70E0) border-box; /* Outer gradient */
        filter: drop-shadow(1px 1px 4px #2B70E0);

        &:focus {
            outline: 2px solid #2B87FF;
            outline-offset: 2px;
        }
    }

    .copy-btn {
        ::v-deep .fe-button {
            margin: 0 !important;
        }
    }

    .close-icon:focus {
        outline: 2px solid #2B87FF;
        outline-offset: 2px;
    }

    .explain-it {
        &-card {
            ::v-deep .v-card__text {
                padding: 0 !important;
                flex-direction: column;
                display: flex;
            }
        }
    }
</style>
